import type { OrderEventData, PendingOrderType } from '../../bot/types/chartData'
import { PENDING_TYPE_LABEL } from '../../bot/utils/pendingOrders'
import type { WalkthroughTrade } from './walkthroughTrades'

export type UnfilledOutcome = 'cancelled' | 'expired' | 'rejected'

/**
 * One History row for a pending order that was primed and never filled: the evidence that the
 * code placed it, and what became of it. A filled order is a trade and lives in the trade rows.
 */
export interface UnfilledOrderRow {
  /** The ending event's id — unique even for a rejected placement, whose ticket is 0. */
  key: string
  /** The engine's order ticket; 0 when the placement was refused before one was issued. */
  ticket: number
  /** The id the strategy code saw — what `buy_limit()` returned, what `cancel_order()` took. */
  orderId: number
  orderType: PendingOrderType | null
  label: string
  side: 'BUY' | 'SELL' | null
  price: number | null
  volume: number | null
  /** From the placing event; a rejection never carried stops to the engine. */
  sl: number | null
  tp: number | null
  /** When the order was placed — for a rejection, the moment it was refused. */
  placedTs: number
  /** When it was cancelled, expired or rejected. */
  endedTs: number
  outcome: UnfilledOutcome
  /** The cancel's detail (`strategy`), the rejection's reason; null for an expiry. */
  reason: string | null
  /** Whole bars the order rested before it ended; null for a rejection or an unknown timeframe. */
  barsPending: number | null
}

type PlacedEvent = Extract<OrderEventData, { kind: 'order_placed' }>

/**
 * The orders that ended without filling at or before the playhead, newest ending first.
 *
 * A refused cancel (`order_rejected` with `rejectKind: 'cancel'`, the engine's `not_found` for
 * an id it did not know) is not an order and is left out, as the server's walkthrough report
 * leaves it out of its counts.
 */
export function buildUnfilledOrders(
  activity: OrderEventData[],
  playheadTs: number,
  tfSec: number,
): UnfilledOrderRow[] {
  const placed = new Map<number, PlacedEvent>()
  for (const a of activity) if (a.kind === 'order_placed') placed.set(a.ticket, a)

  const rows: UnfilledOrderRow[] = []
  for (const a of activity) {
    if (a.time > playheadTs) continue
    if (a.kind === 'order_cancelled' || a.kind === 'order_expired') {
      const p = placed.get(a.ticket)
      const placedTs = p?.time ?? a.time
      rows.push({
        key: `o:${a.eventId}`,
        ticket: a.ticket,
        orderId: a.orderId,
        orderType: a.orderType,
        label: PENDING_TYPE_LABEL[a.orderType],
        side: a.side,
        price: a.price,
        volume: a.volume,
        sl: p?.sl ?? null,
        tp: p?.tp ?? null,
        placedTs,
        endedTs: a.time,
        outcome: a.kind === 'order_cancelled' ? 'cancelled' : 'expired',
        reason: a.kind === 'order_cancelled' ? a.detail || null : null,
        barsPending: tfSec > 0 ? Math.max(0, Math.round((a.time - placedTs) / tfSec)) : null,
      })
    } else if (a.kind === 'order_rejected' && a.rejectKind === 'place') {
      rows.push({
        key: `o:${a.eventId}`,
        ticket: a.ticket,
        orderId: a.orderId,
        orderType: a.orderType,
        label: a.orderType ? PENDING_TYPE_LABEL[a.orderType] : '—',
        side: a.side,
        price: a.price,
        volume: a.volume,
        sl: null,
        tp: null,
        placedTs: a.time,
        endedTs: a.time,
        outcome: 'rejected',
        reason: a.reason || null,
        barsPending: null,
      })
    }
  }
  rows.sort((x, y) => y.endedTs - x.endedTs || y.ticket - x.ticket)
  return rows
}

/** A History row: a closed trade or an order that never filled, in one list by when it ended. */
export type HistoryRow =
  | { kind: 'trade'; endTs: number; trade: WalkthroughTrade }
  | { kind: 'order'; endTs: number; order: UnfilledOrderRow }

/** Closed trades and unfilled orders interleaved, newest ending first; a trade leads on a tie. */
export function buildHistoryRows(closed: WalkthroughTrade[], unfilled: UnfilledOrderRow[]): HistoryRow[] {
  const rank = (r: HistoryRow) => (r.kind === 'trade' ? 0 : 1)
  const rows: HistoryRow[] = [
    ...closed.map((trade): HistoryRow => ({ kind: 'trade', endTs: trade.exitTs ?? trade.entryTs, trade })),
    ...unfilled.map((order): HistoryRow => ({ kind: 'order', endTs: order.endedTs, order })),
  ]
  rows.sort((a, b) => b.endTs - a.endTs || rank(a) - rank(b))
  return rows
}
